// 商城管理 — bundle 编排器列表页 (benefit-bundle-storefront 组4).
//
// 后端 /admin/v1/benefit/bundles. bundle 按 App 记账 → 顶部 App 选择器.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { listApps, type App } from '@/api/apps';
import {
  listBundles,
  createBundle,
  updateBundle,
  type Bundle,
  type BundleStatus,
} from '@/api/bundles';
import { formatDateTime } from '@/utils/format';

const STATUS_META: Record<BundleStatus, { label: string; color: string }> = {
  draft: { label: '草稿', color: 'default' },
  active: { label: '已上架', color: 'green' },
  inactive: { label: '已下架', color: 'red' },
};

export function MallListPage() {
  const [apps, setApps] = useState<App[]>([]);
  const [appId, setAppId] = useState<number | undefined>(undefined);
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    listApps({ page_size: 100 })
      .then((res) => {
        setApps(res.data);
        const first = res.data.find((a) => a.status === 'active') ?? res.data[0];
        if (first) setAppId((prev) => prev ?? first.id);
      })
      .catch((e) => message.error(e instanceof Error ? e.message : '加载 App 列表失败'));
  }, []);

  const load = () => {
    if (!appId) return;
    setLoading(true);
    listBundles(appId)
      .then((res) => setBundles(res.data))
      .catch((e) => message.error(e instanceof Error ? e.message : '加载 bundle 失败'))
      .finally(() => setLoading(false));
  };
  useEffect(load, [appId]);

  const appOptions = useMemo(
    () => apps.map((a) => ({ value: a.id, label: `#${a.id} ${a.name} (${a.slug})` })),
    [apps],
  );

  const handleCreate = async () => {
    const v = await form.validateFields();
    if (!appId) return;
    try {
      await createBundle({
        app_id: appId,
        name: v.name,
        slug: v.slug || undefined,
        monthly_price_cents: Math.round((v.monthly_price_yuan || 0) * 100),
      });
      message.success('已创建');
      setCreateOpen(false);
      form.resetFields();
      load();
    } catch (e) {
      message.error(e instanceof Error ? e.message : '创建失败');
    }
  };

  const toggleStatus = async (row: Bundle) => {
    const next: BundleStatus = row.status === 'active' ? 'inactive' : 'active';
    try {
      await updateBundle(row.id, { status: next });
      message.success(next === 'active' ? '已上架' : '已下架');
      load();
    } catch (e) {
      // 激活时后端会校验结构(空选择组/fixed 非 1 候选)→ 400
      message.error(e instanceof Error ? e.message : '操作失败');
    }
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space wrap>
        <Typography.Text strong>App</Typography.Text>
        <Select
          style={{ width: 280 }}
          value={appId}
          options={appOptions}
          onChange={setAppId}
          showSearch
          optionFilterProp="label"
          placeholder="选择 App"
        />
        <Button type="primary" icon={<PlusOutlined />} disabled={!appId} onClick={() => setCreateOpen(true)}>
          新建 bundle
        </Button>
      </Space>

      {!appId ? (
        <Alert type="info" showIcon message="请选择 App 后查看该 App 的组合权益包" />
      ) : (
        <Table<Bundle>
          rowKey="id"
          loading={loading}
          dataSource={bundles}
          pagination={false}
          columns={[
            {
              title: '名称',
              dataIndex: 'name',
              render: (v: string, row) => <Link to={`/mall/${row.id}`}>{v}</Link>,
            },
            { title: 'slug', dataIndex: 'slug', render: (v?: string | null) => v || '-' },
            {
              title: '月付价',
              dataIndex: 'monthly_price_cents',
              align: 'right',
              render: (c: number) => `¥${(c / 100).toFixed(2)}`,
            },
            {
              title: '状态',
              dataIndex: 'status',
              render: (v: BundleStatus) => <Tag color={STATUS_META[v]?.color}>{STATUS_META[v]?.label ?? v}</Tag>,
            },
            { title: '创建时间', dataIndex: 'created_at', render: (v: string) => formatDateTime(v) },
            {
              title: '操作',
              key: 'actions',
              render: (_, row) => (
                <Space>
                  <Link to={`/mall/${row.id}`}>编排</Link>
                  <Popconfirm
                    title={row.status === 'active' ? '下架该 bundle?' : '上架该 bundle?'}
                    description={
                      row.status === 'active'
                        ? '下架后推广页不再展示; 已购权益不受影响.'
                        : '上架前后端会校验结构(每个固定项恰好1候选、每个选择组≥1候选).'
                    }
                    onConfirm={() => toggleStatus(row)}
                    okText={row.status === 'active' ? '下架' : '上架'}
                    cancelText="取消"
                  >
                    <Button type="link" size="small" style={{ padding: 0 }}>
                      {row.status === 'active' ? '下架' : '上架'}
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      )}

      <Modal
        title="新建 bundle"
        open={createOpen}
        onOk={handleCreate}
        onCancel={() => setCreateOpen(false)}
        okText="创建"
        cancelText="取消"
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="如: 生活权益月付包" />
          </Form.Item>
          <Form.Item name="slug" label="slug (推广页短标识, 可空)">
            <Input placeholder="如: life-monthly" />
          </Form.Item>
          <Form.Item
            name="monthly_price_yuan"
            label="月付价 (元)"
            rules={[{ required: true, message: '请输入月付价' }]}
          >
            <InputNumber min={0} precision={2} style={{ width: '100%' }} addonBefore="¥" />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
